/**
 * Adapter d'impression utilisant window.print()
 * Fallback quand Web USB non disponible ou pour tests
 */

import { logger } from '../../utils/logger';
import {
  PrinterAdapter,
  PrintJob,
  PrinterStatus,
  PrinterError,
} from './PrinterAdapter';

export class BrowserPrintAdapter implements PrinterAdapter {
  readonly modelName = 'Impression navigateur';
  readonly connectionType = 'BROWSER' as const;

  private connected = false;

  /**
   * "Connecter" (juste marquer comme prêt)
   */
  async connect(): Promise<void> {
    this.connected = true;
    logger.log('[BrowserPrintAdapter] Mode impression navigateur activé');
  }

  /**
   * Déconnecter
   */
  async disconnect(): Promise<void> {
    this.connected = false;
  }

  /**
   * Vérifier si connecté
   */
  isConnected(): boolean {
    return this.connected;
  }

  /**
   * Obtenir le statut
   */
  async getStatus(): Promise<PrinterStatus> {
    return {
      connected: this.connected,
      online: this.connected,
      paperStatus: 'ok',
    };
  }

  /**
   * Imprimer un ticket via window.print()
   */
  async print(job: PrintJob): Promise<void> {
    if (!this.isConnected()) {
      throw new PrinterError(
        'Adapter d\'impression non initialisé',
        'NOT_CONNECTED'
      );
    }

    try {
      // Générer le HTML du ticket
      const html = this.generateHTML(job);

      // Créer un iframe caché pour l'impression (plus fiable que popup)
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      // Écrire le contenu dans l'iframe
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new PrinterError(
          'Impossible d\'accéder au document d\'impression',
          'PRINT_ERROR'
        );
      }

      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      // Attendre que le contenu soit chargé
      await new Promise<void>((resolve) => {
        if (iframeDoc.readyState === 'complete') {
          resolve();
        } else {
          iframe.addEventListener('load', () => resolve());
        }
      });

      // Déclencher l'impression
      iframe.contentWindow?.print();

      // Supprimer l'iframe après un délai
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);

      logger.log('[BrowserPrintAdapter] Impression lancée via iframe');
    } catch (error) {
      throw new PrinterError(
        'Erreur lors de l\'impression',
        'PRINT_ERROR',
        error as Error
      );
    }
  }

  /**
   * Formatter une ligne en colonnes (inspiré de foliespos)
   * @param left Texte à gauche
   * @param right Texte à droite
   * @param width Largeur totale en caractères (42 pour ticket 80mm)
   */
  private colLine(left: string, right: string, width = 42): string {
    const gap = 2;
    const avail = Math.max(0, width - right.length - gap);
    const leftTrimmed = left.slice(0, avail).padEnd(avail, ' ');
    return leftTrimmed + ' '.repeat(gap) + right;
  }

  /**
   * Générer le HTML du ticket
   */
  private generateHTML(job: PrintJob): string {
    const lines = job.lines
      .map((line) => {
        let className = '';
        let content = line.content || '';

        // Alignement
        if (line.align === 'center') className += ' text-center';
        if (line.align === 'right') className += ' text-right';

        // Styles
        if (line.bold) className += ' font-bold';
        if (line.underline) className += ' underline';

        // Taille
        if (line.fontSize === 'large') className += ' text-xl';
        if (line.fontSize === 'xlarge') className += ' text-2xl';
        if (line.fontSize === 'small') className += ' text-xs';

        switch (line.type) {
          case 'text':
            // Si le contenu contient "=" (format "label = valeur")
            // Améliorer le formatage en colonnes style foliespos
            if (content.includes('=')) {
              const parts = content.split('=');
              if (parts.length === 2) {
                const left = parts[0].trim();
                const right = parts[1].trim();
                const formatted = this.colLine(left, right);
                return `<div class="font-mono ${className}">${this.escapeHtml(formatted)}</div>`;
              }
            }
            return `<div class="${className}">${this.escapeHtml(content)}</div>`;

          case 'separator':
            return `<div class="separator ${className}">────────────────────────────────────────</div>`;

          case 'feed':
            return '<br>'.repeat(line.feeds || 1);

          case 'barcode':
            // Afficher le code en texte (barcode réel nécessiterait une lib)
            return `<div class="text-center font-mono text-sm">${this.escapeHtml(
              content
            )}</div>`;

          case 'qrcode':
            // TODO: Utiliser une lib pour générer QR code
            return `<div class="text-center text-xs">QR: ${this.escapeHtml(
              content
            )}</div>`;

          default:
            return '';
        }
      })
      .join('\n');

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Ticket - ${job.metadata?.saleNumber || ''}</title>
  <style>
    /* Configuration pour impression ticket 80mm */
    @page {
      size: 80mm auto;
      margin: 5mm 0;
    }

    @media print {
      body {
        margin: 0;
        padding: 0;
        width: 80mm;
      }

      /* Masquer les éléments non nécessaires */
      @page {
        margin: 0;
      }
    }

    /* Styles pour Safari/iPad */
    body {
      font-family: 'Courier New', 'Monaco', monospace;
      font-size: 12px;
      line-height: 1.3;
      width: 80mm;
      max-width: 80mm;
      margin: 0 auto;
      padding: 5mm;
      background: white;
      color: black;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .text-center { text-align: center; }
    .text-right { text-align: right; }
    .text-left { text-align: left; }
    .font-bold { font-weight: bold; }
    .underline { text-decoration: underline; }
    .text-xs { font-size: 9px; }
    .text-sm { font-size: 10px; }
    .text-xl { font-size: 14px; }
    .text-2xl { font-size: 16px; }
    .font-mono { font-family: 'Courier New', monospace; }

    /* Éviter les coupures de page */
    * {
      page-break-inside: avoid;
    }
  </style>
</head>
<body>
  ${lines}
</body>
</html>
    `;
  }

  /**
   * Échapper HTML
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
